import React, { useState } from 'react';
import {
  Grid,
  Link,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  IconButton,
} from '@mui/material';
import { Cancel, Edit, Launch, Save } from '@mui/icons-material';
import { roles } from '../../../utils/variables';
import { useApiGet, useApiSend } from '../../../utils/httpClient';
import {
  getSPDChapterTypes,
  getSPDRegions,
  getSPDSponsers,
  getSpdInfo,
  patchSpdInfo,
} from '../../../endpoints/chapters';

const SPDInfo = ({ id, spdAccess }) => {
  const [editName, setEditName] = useState(false);
  const [chapterName, setChapterName] = useState(null);
  const [editChapterType, setEditChapterType] = useState(false);
  const [chapterType, setChapterType] = useState(null);
  const [editRegion, setEditRegion] = useState(false);
  const [region, setRegion] = useState(null);
  const [editSponsor, setEditSponsor] = useState(false);
  const [sponsor, setSponsor] = useState(null);
  const [error, setError] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [editRep, setEditRep] = useState(false);

  const { data: chapterTypes } = useApiGet(
    ['chapter-types'],
    getSPDChapterTypes,
    {
      enabled: true,
      retry: 1,
    }
  );

  const { data: regions } = useApiGet(['regions'], getSPDRegions, {
    enabled: true,
    retry: 1,
  });
  const { data: sponsors } = useApiGet(['sponsors'], getSPDSponsers, {
    enabled: true,
    retry: 1,
  });
  const {
    data: spdInfo,
    isFetching,
    refetch,
  } = useApiGet(
    ['spd-info', id],
    getSpdInfo,
    {
      enabled: true,
      retry: 1,
    },
    {
      id: id,
    }
  );

  const { mutate: patchInfo } = useApiSend(
    patchSpdInfo,
    () => {
      refetch();
      setIsSaving(false);
      setEditRegion(false);
      setEditName(false);
      setEditChapterType(false);
      setEditSponsor(false);
    },
    () => {
      if (error) {
        if (error.message) {
          setError(error.message);
        } else {
          setError('Error updating SPD info');
        }
      }
    }
  );

  const canEdit = () => {
    if (spdAccess) {
      let role = localStorage.getItem('role');
      let rolesAllowed = [roles.Admin, roles.Support];
      return rolesAllowed.includes(role);
    }
    return false;
  };

  const handleEditName = () => {
    setEditName(!editName);
    setChapterName(spdInfo.name);
  };

  const handleEditNameCancel = () => {
    setEditName(false);
    setChapterName(null);
  };

  const handleEditNameSave = () => {
    setError(null);
    setIsSaving(true);
    patchInfo({
      formId: id,
      name: chapterName,
    });
  };

  const handleEditChapterType = () => {
    setEditChapterType(!editChapterType);
    setChapterType(spdInfo.chapter_type_id);
  };

  const handleEditChapterTypeCancel = () => {
    setEditChapterType(false);
    setChapterType(null);
  };
  const handleEditChapterTypeSave = () => {
    setError(null);
    setIsSaving(true);
    patchInfo({
      formId: id,
      chapter_type_id: chapterType,
    });
  };

  const handleEditRegion = () => {
    setEditRegion(!editRegion);
    setRegion(spdInfo.region_id);
  };

  const handleEditRegionCancel = () => {
    setEditRegion(false);
    setRegion(null);
  };

  const handleEditRegionSave = () => {
    setError(null);
    setIsSaving(true);
    patchInfo({
      formId: id,
      region_id: region,
    });
  };

  const handleEditSponsor = () => {
    setEditSponsor(!editSponsor);
    setSponsor(spdInfo.sponsor_id);
  };

  const handleEditSponsorCancel = () => {
    setEditSponsor(false);
    setSponsor(null);
  };

  const handleEditSponsorSave = () => {
    spdInfo.sponsor_id = sponsor;
    setError(null);
    setIsSaving(true);
    patchInfo({
      formId: id,
      sponsor_id: sponsor,
    });
  };

  if (isFetching) {
    return <div>Loading...</div>;
  }

  if (!spdInfo) {
    return <div>No SPD info found</div>;
  }

  return (
    <Grid
      container
      paddingRight={4}
      paddingLeft={4}
      paddingTop={2}
      paddingBottom={3}
    >
      <Grid item xs={12} padding>
        {editName ? (
          <Grid container item xs={12}>
            <TextField
              value={chapterName}
              onChange={(e) => setChapterName(e.target.value)}
              label="Chapter Name"
              variant="outlined"
              size="small"
            />
            <IconButton
              onClick={() => handleEditNameSave()}
              sx={{ padding: 0, marginLeft: 1 }}
              disabled={isSaving}
            >
              <Save color="green" sx={{ fontSize: 30 }} />
            </IconButton>
            <IconButton
              onClick={() => handleEditNameCancel()}
              sx={{ padding: 0, marginLeft: 1 }}
              disabled={isSaving}
            >
              <Cancel color="primary" sx={{ fontSize: 30 }} />
            </IconButton>
          </Grid>
        ) : (
          <Typography variant="body1">
            <b>{'Name:'}</b> {spdInfo.name}
            {canEdit() && (
              <IconButton
                onClick={() => handleEditName()}
                sx={{ marginLeft: 1, pl: 1, pr: 1, pt: 0, pb: 0 }}
                disabled={isSaving}
              >
                <Edit color="blue" sx={{ fontSize: 18 }} />
              </IconButton>
            )}
          </Typography>
        )}
      </Grid>
      <Grid item xs={12} padding>
        {editChapterType ? (
          <Grid container item xs={12} padding>
            <Grid item xs={8}>
              <FormControl fullWidth>
                <InputLabel id="chapter-type-label">Chapter Type</InputLabel>
                <Select
                  value={chapterType}
                  onChange={(e) => setChapterType(e.target.value)}
                  label="Chapter Type"
                  size="small"
                >
                  {chapterTypes.map((type) => (
                    <MenuItem key={type.id} value={type.id}>
                      {type.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={4}>
              <IconButton
                onClick={() => handleEditChapterTypeSave()}
                sx={{ padding: 0, marginLeft: 1 }}
                disabled={isSaving}
              >
                <Save color="green" sx={{ fontSize: 30 }} />
              </IconButton>
              <IconButton
                onClick={() => handleEditChapterTypeCancel()}
                sx={{ padding: 0, marginLeft: 1 }}
                disabled={isSaving}
              >
                <Cancel color="primary" sx={{ fontSize: 30 }} />
              </IconButton>
            </Grid>
          </Grid>
        ) : (
          <Typography variant="body1">
            <b>{'Chapter Type:'}</b> {spdInfo?.chapter_type}
            {canEdit() && (
              <IconButton
                onClick={() => handleEditChapterType()}
                sx={{ marginLeft: 1, pl: 1, pr: 1, pt: 0, pb: 0 }}
                disabled={isSaving}
              >
                <Edit color="blue" sx={{ fontSize: 18 }} />
              </IconButton>
            )}
          </Typography>
        )}
      </Grid>
      <Grid item xs={12} padding>
        {editRegion ? (
          <Grid container item xs={12} padding>
            <Grid item xs={8}>
              <FormControl fullWidth>
                <InputLabel id="region-label">Region</InputLabel>
                <Select
                  value={region}
                  onChange={(e) => setRegion(e.target.value)}
                  label="Region"
                  size="small"
                >
                  {regions.map((region) => (
                    <MenuItem key={region.id} value={region.id}>
                      {region.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={4}>
              <IconButton
                onClick={() => handleEditRegionSave()}
                sx={{ padding: 0, marginLeft: 1 }}
                disabled={isSaving}
              >
                <Save color="green" sx={{ fontSize: 30 }} />
              </IconButton>
              <IconButton
                onClick={() => handleEditRegionCancel()}
                sx={{ padding: 0, marginLeft: 1 }}
                disabled={isSaving}
              >
                <Cancel color="primary" sx={{ fontSize: 30 }} />
              </IconButton>
            </Grid>
          </Grid>
        ) : (
          <Typography variant="body1">
            <b>{'Membership Region:'}</b> {spdInfo.region_name}
            {canEdit() && (
              <IconButton
                onClick={() => handleEditRegion()}
                sx={{ marginLeft: 1, pl: 1, pr: 1, pt: 0, pb: 0 }}
                disabled={isSaving}
              >
                <Edit color="blue" sx={{ fontSize: 18 }} />
              </IconButton>
            )}
          </Typography>
        )}
      </Grid>
      <Grid item xs={12} padding>
        {editSponsor ? (
          <Grid container item xs={12} padding>
            <Grid item xs={8}>
              <FormControl fullWidth>
                <InputLabel id="sponsor-label">EB Liaison</InputLabel>
                <Select
                  value={sponsor}
                  onChange={(e) => setSponsor(e.target.value)}
                  label="EB Liaison"
                  size="small"
                >
                  {sponsors.map((sponsor) => (
                    <MenuItem key={sponsor.id} value={sponsor.id}>
                      {sponsor.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={4}>
              <IconButton
                onClick={() => handleEditSponsorSave()}
                sx={{ padding: 0, marginLeft: 1 }}
                disabled={isSaving}
              >
                <Save color="green" sx={{ fontSize: 30 }} />
              </IconButton>
              <IconButton
                onClick={() => handleEditSponsorCancel()}
                sx={{ padding: 0, marginLeft: 1 }}
                disabled={isSaving}
              >
                <Cancel color="primary" sx={{ fontSize: 30 }} />
              </IconButton>
            </Grid>
          </Grid>
        ) : (
          <Typography variant="body1">
            <b>{'EB Liaison:'}</b> {spdInfo.sponsor}
            {canEdit() && (
              <IconButton
                onClick={() => handleEditSponsor()}
                sx={{ marginLeft: 1, pl: 1, pr: 1, pt: 0, pb: 0 }}
                disabled={isSaving}
              >
                <Edit color="blue" sx={{ fontSize: 18 }} />
              </IconButton>
            )}
          </Typography>
        )}
      </Grid>
      <Grid item xs={12} padding>
        {editRep ? (
          <Grid container item xs={12} padding>
            <Grid item xs={8}>
              <FormControl fullWidth>
                <InputLabel id="intl-label">
                  International Chapter Representative
                </InputLabel>
                <Select
                  value={sponsor}
                  onChange={(e) => setSponsor(e.target.value)}
                  label=" International Chapter Representative"
                  size="small"
                >
                  {sponsors.map((sponsor) => (
                    <MenuItem key={sponsor.id} value={sponsor.id}>
                      {sponsor.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={4}>
              <IconButton
                onClick={() => handleEditSponsorSave()}
                sx={{ padding: 0, marginLeft: 1 }}
                disabled={isSaving}
              >
                <Save color="green" sx={{ fontSize: 30 }} />
              </IconButton>
              <IconButton
                onClick={() => handleEditSponsorCancel()}
                sx={{ padding: 0, marginLeft: 1 }}
                disabled={isSaving}
              >
                <Cancel color="primary" sx={{ fontSize: 30 }} />
              </IconButton>
            </Grid>
          </Grid>
        ) : (
          <Typography variant="body1">
            <b>{'International Chapter Representative:'}</b>{' '}
            {spdInfo.international_representative}
            {canEdit() && (
              <IconButton
                onClick={() => console.log('Edit rep clicked')}
                sx={{ marginLeft: 1, pl: 1, pr: 1, pt: 0, pb: 0 }}
                disabled={isSaving}
              >
                <Edit color="blue" sx={{ fontSize: 18 }} />
              </IconButton>
            )}
          </Typography>
        )}
      </Grid>
      <Grid item xs={12} padding>
        <Typography variant="body1">
          <b>{'Active Chapters:'}</b> {spdInfo.active_chapters}{' '}
          <Link
            target="_blank"
            rel="noopener noreferrer"
            href={`/local-chapters?spd=${id}&status=active`}
            paddingLeft={3}
          >
            <Launch sx={{ fontSize: 14, pr: 1 }} />
            View Chapters
          </Link>
        </Typography>
      </Grid>
      <Grid item xs={12} padding>
        <Typography variant="body1">
          <b>{'Active Members:'}</b> {spdInfo.active_members}
        </Typography>
      </Grid>
    </Grid>
  );
};

export default SPDInfo;
